// src/components/Drivers/AjouterDriver.js
import React, { useEffect, useState } from "react";
import Select from "react-select";
import validator from "validator";
import { Button, Card, Form, Container, Row, Col } from "react-bootstrap";
import { useParams, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  driverAdded,
  driverGetById,
  driverUpdated,
} from "../../../Redux/driversSlice";
import { fetchCars } from "../../../Redux/carsSlice";

function AjouterDriver() {
  const notify = (type, msg) => {
    if (type === 1) {
      toast.success(
        <div>
          <i className="fas fa-check-circle mr-2"></i>
          {msg}
        </div>,
        {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
    } else {
      toast.error(
        <div>
          <i className="fas fa-exclamation-circle mr-2"></i>
          {msg}
        </div>,
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
    }
  };

  const dispatch = useDispatch();
  const navigate = useHistory();
  const location = useParams();

  // Form states
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [cin, setCin] = useState("");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [active, setActive] = useState(true);
  const [carId, setCarId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Car options
  const [carOptions, setCarOptions] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);

  // Load cars for select dropdown
  useEffect(() => {
    setLoading(true);
    dispatch(fetchCars())
      .then((response) => {
        const carsList = response.payload;
        const options = carsList.map((car) => ({
          value: car.id,
          label: `${car.brand || ''} ${car.model || ''} - ${car.registration}`.trim(),
        }));
        setCarOptions(options);
        setLoading(false);
      })
      .catch((err) => {
        notify(2, "Erreur lors du chargement des véhicules");
        setLoading(false);
      });

    // Load driver data if editing
    if (location.id) {
      setLoading(true);
      dispatch(driverGetById(location.id))
        .then((response) => {
          const driver = response.payload;
          setFirstName(driver.firstName || "");
          setLastName(driver.lastName || "");
          setPhone(driver.phone || "");
          setEmail(driver.email || "");
          setCin(driver.cin || "");
          setLicenseNumber(driver.licenseNumber || "");
          setActive(driver.active !== undefined ? driver.active : true);

          if (driver.car) {
            setSelectedCar({
              value: driver.car.id,
              label: `${driver.car.brand || ''} ${driver.car.model || ''} - ${driver.car.registration}`.trim(),
            });
            setCarId(driver.car.id);
          }
          setLoading(false);
        })
        .catch((err) => {
          notify(2, "Erreur lors du chargement du chauffeur");
          setLoading(false);
        });
    }
  }, [dispatch, location.id]);

  function listeDrivers() {
    navigate.push("/drivers/list");
  }

  async function submitForm(event) {
    event.preventDefault();

    const required = document.getElementsByClassName("required");
    let isValid = true;

    // Clear previous errors
    document.querySelectorAll(".error").forEach((el) => (el.innerHTML = ""));
    document
      .querySelectorAll(".required")
      .forEach((el) => (el.style.borderColor = "#ccc"));
    document.querySelectorAll(".form-control").forEach((el) => {
      el.style.borderColor = "";
    });

    // Validate required fields
    for (let i = 0; i < required.length; i++) {
      if (validator.isEmpty(required[i].value)) {
        required[i].style.borderColor = "red";
        const errorElement = document.getElementsByClassName("error")[i];
        if (errorElement) {
          errorElement.innerHTML = required[i].name + " est obligatoire";
        }
        notify(2, required[i].name + " doit être non vide");
        isValid = false;
      }
    }

    // Validate email format if provided
    if (email && !validator.isEmail(email)) {
      notify(2, "Format d'email invalide");
      isValid = false;
    }

    // Validate CIN format
    if (cin) {
      if (!validator.isLength(cin, { min: 8, max: 12 })) {
        notify(2, "Le CIN doit contenir entre 8 et 12 caractères");
        isValid = false;
      }
      if (!validator.matches(cin, /^[A-Z0-9]+$/)) {
        notify(2, "Le CIN ne peut contenir que des lettres majuscules et des chiffres");
        isValid = false;
      }
    }

    // Validate phone if provided
    if (phone && !validator.isMobilePhone(phone, "any")) {
      notify(2, "Format de téléphone invalide");
      isValid = false;
    }

    // Validate license number if provided
    if (licenseNumber && !validator.isLength(licenseNumber, { min: 5 })) {
      notify(2, "Le numéro de permis doit contenir au moins 5 caractères");
      isValid = false;
    }

    if (isValid) {
      setLoading(true);
      const driverData = {
        firstName,
        lastName,
        phone: phone || null,
        email: email || null,
        cin: cin || null,
        licenseNumber: licenseNumber || null,
        active,
        carId: carId || null,
      };

      try {
        let result;
        if (location.id) {
          result = await dispatch(
            driverUpdated({ id: parseInt(location.id), ...driverData })
          );
        } else {
          result = await dispatch(driverAdded(driverData));
        }

        // Check if the action was rejected
        if (driverAdded.rejected.match(result) || driverUpdated.rejected.match(result)) {
          // Extract error message from the response
          let errorMessage = "Erreur lors de l'enregistrement";
          
          if (result.payload) {
            // The error object from rejectWithValue is in payload
            errorMessage = result.payload.message || errorMessage;
          }
          
          // Show the error message
          notify(2, errorMessage);
          
          // Highlight the CIN field if it's a CIN error
          if (errorMessage.toLowerCase().includes('cin')) {
            document.querySelector('input[placeholder*="Carte"]')?.style.setProperty('border-color', 'red', 'important');
          }
          // Highlight the email field if it's an email error
          if (errorMessage.toLowerCase().includes('email')) {
            document.querySelector('input[type="email"]')?.style.setProperty('border-color', 'red', 'important');
          }
          // Highlight the license field if it's a license error
          if (errorMessage.toLowerCase().includes('permis') || errorMessage.toLowerCase().includes('license')) {
            document.querySelector('input[placeholder*="permis"]')?.style.setProperty('border-color', 'red', 'important');
          }
        } else if (result.payload) {
          notify(
            1,
            location.id ? "Modification effectuée avec succès" : "Insertion effectuée avec succès"
          );
          setTimeout(() => {
            listeDrivers();
          }, 1500);
        }
      } catch (error) {
        console.error("Submission error:", error);
        notify(2, error.message || "Erreur lors de l'enregistrement");
      } finally {
        setLoading(false);
      }
    }
  }

  return (
    <Container fluid>
      <ToastContainer />
      <div className="section-image">
        <Container>
          <Row>
            <Col md="12">
              <Button
                className="btn-wd btn-outline mr-1 float-left"
                type="button"
                variant="info"
                onClick={listeDrivers}
                disabled={loading}
              >
                <span className="btn-label">
                  <i className="fas fa-list"></i>
                </span>
                Retour à la liste
              </Button>
            </Col>
          </Row>
          <Row>
            <Col md="12">
              <Form onSubmit={submitForm}>
                <Card>
                  <Card.Header>
                    <Card.Title as="h4">
                      {!location.id
                        ? "Ajouter un chauffeur"
                        : "Modifier le chauffeur"}
                    </Card.Title>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col className="pr-1" md="6">
                        <Form.Group>
                          <label>Prénom *</label>
                          <Form.Control
                            value={firstName}
                            placeholder="Prénom"
                            name="Prénom"
                            className="required"
                            type="text"
                            onChange={(e) => setFirstName(e.target.value)}
                            disabled={loading}
                          />
                          <div className="error text-danger"></div>
                        </Form.Group>
                      </Col>
                      <Col className="pl-1" md="6">
                        <Form.Group>
                          <label>Nom *</label>
                          <Form.Control
                            value={lastName}
                            placeholder="Nom"
                            name="Nom"
                            className="required"
                            type="text"
                            onChange={(e) => setLastName(e.target.value)}
                            disabled={loading}
                          />
                          <div className="error text-danger"></div>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col className="pr-1" md="4">
                        <Form.Group>
                          <label>Téléphone</label>
                          <Form.Control
                            value={phone}
                            placeholder="Téléphone"
                            type="tel"
                            onChange={(e) => setPhone(e.target.value)}
                            disabled={loading}
                          />
                        </Form.Group>
                      </Col>
                      <Col className="pr-1" md="4">
                        <Form.Group>
                          <label>Email</label>
                          <Form.Control
                            value={email}
                            placeholder="Email"
                            type="email"
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                          />
                          <small className="text-muted">
                            Utilisé comme identifiant de connexion
                          </small>
                        </Form.Group>
                      </Col>
                      <Col className="pl-1" md="4">
                        <Form.Group>
                          <label>CIN</label>
                          <Form.Control
                            value={cin}
                            placeholder="Carte d'identité nationale"
                            type="text"
                            onChange={(e) => setCin(e.target.value.toUpperCase())}
                            disabled={loading}
                          />
                          <small className="text-muted">
                            Utilisé comme mot de passe initial
                          </small>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col className="pr-1" md="6">
                        <Form.Group>
                          <label>Numéro de permis</label>
                          <Form.Control
                            value={licenseNumber}
                            placeholder="Numéro de permis de conduire"
                            type="text"
                            onChange={(e) => setLicenseNumber(e.target.value)}
                            disabled={loading}
                          />
                        </Form.Group>
                      </Col>
                      <Col className="pl-1" md="6">
                        <Form.Group>
                          <label>Véhicule assigné</label>
                          <Select
                            placeholder="Sélectionner un véhicule"
                            className="react-select primary"
                            classNamePrefix="react-select"
                            value={selectedCar}
                            isClearable
                            isDisabled={loading}
                            onChange={(value) => {
                              setSelectedCar(value);
                              setCarId(value ? value.value : null);
                            }}
                            options={carOptions}
                            noOptionsMessage={() => "Aucun véhicule disponible"}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col className="pr-1" md="6">
                        <Form.Group>
                          <Form.Check
                            type="switch"
                            id="active-switch"
                            label="Chauffeur actif"
                            checked={active}
                            onChange={(e) => setActive(e.target.checked)}
                            disabled={loading}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Button
                      className="btn-fill pull-right"
                      type="submit"
                      variant="info"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          En cours...
                        </>
                      ) : (
                        "Enregistrer"
                      )}
                    </Button>
                    <div className="clearfix"></div>
                  </Card.Body>
                </Card>
              </Form>
            </Col>
          </Row>
        </Container>
      </div>
    </Container>
  );
}

export default AjouterDriver;